import { supabase } from './supabaseClient';
import { UserRole } from '../../types/admin';

/**
 * Récupère le rôle de l'utilisateur connecté
 */
export const getUserRole = async (userId: string): Promise<{ data: UserRole | null; error: any }> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .single();

    if (error) throw error;

    return { data: data?.role as UserRole || 'client', error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération du rôle:', error);
    return { data: null, error };
  }
};

/**
 * Met à jour le rôle d'un utilisateur (admin seulement)
 */
export const updateUserRole = async (
  userId: string, 
  newRole: UserRole,
  adminUserId: string
): Promise<{ data: any; error: any }> => {
  try {
    // Vérifier que l'utilisateur qui fait la demande est admin
    const { data: adminCheck } = await getUserRole(adminUserId);
    if (adminCheck !== 'admin') {
      throw new Error('Accès refusé : seuls les admins peuvent modifier les rôles');
    }

    const { data, error } = await supabase
      .from('profiles')
      .update({ role: newRole })
      .eq('id', userId)
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Erreur lors de la mise à jour du rôle:', error);
    return { data: null, error };
  }
};

/**
 * Récupère tous les utilisateurs avec leurs rôles (admin seulement)
 */
export const getAllUsersWithRoles = async (adminUserId: string) => {
  try {
    // Vérifier que l'utilisateur qui fait la demande est admin
    const { data: adminCheck } = await getUserRole(adminUserId);
    if (adminCheck !== 'admin') {
      throw new Error('Accès refusé : seuls les admins peuvent voir tous les utilisateurs');
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, client_name, email, role, is_active, created_at, updated_at')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Erreur lors de la récupération des utilisateurs:', error);
    return { data: null, error };
  }
};

/**
 * Vérifie si l'utilisateur a les permissions pour une action donnée
 */
export const checkPermission = (userRole: UserRole, requiredRole: UserRole): boolean => {
  const roleHierarchy: Record<UserRole, number> = {
    'client': 1,
    'caller': 2,
    'admin': 3,
  };

  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
};

/**
 * Hook personnalisé pour vérifier les permissions
 */
export const usePermissions = (userRole: UserRole) => {
  return {
    canViewAdminDashboard: checkPermission(userRole, 'admin'),
    canViewCallerDashboard: checkPermission(userRole, 'caller'),
    canManageUsers: checkPermission(userRole, 'admin'),
    canCreateRdv: checkPermission(userRole, 'caller'),
    canViewAllRdv: checkPermission(userRole, 'caller'),
    canManageCampaigns: checkPermission(userRole, 'client'),
  };
}; 